/* istanbul ignore file */
import { useContext } from 'react'
import allytm from '@ally/allytm'
import { Step, useSpendingBucketCategories } from '../../services'
import {
  SpendingBucketCategory,
  SpendingBucketFormValues,
  Merchant,
  SpendingBucket,
} from '../../types'
import { BUCKET_FREQUENCY_MAP } from '../frequencyMap'
import { FormValues } from '../../components/Checking/SpendingBuckets/DistributeModal'
import { SpendingBucketsContext } from '../../services/SpendingBucketsProvider'

export type SpendingBucketsFlow = 'createBucket' | 'editBucket'

export interface TaggingPrefix {
  taggingPrefix: SpendingBucketsFlow
}

const lookupCategoryName = (
  categoryId?: string,
  categories?: SpendingBucketCategory[],
): string | undefined => {
  return (
    categories?.find(cat => cat.categoryId === categoryId)?.categoryName ||
    'Other'
  )
}

const lookupFrequency = (frequencyCode: string): string => {
  return BUCKET_FREQUENCY_MAP[frequencyCode]
}

const getMerchantNames = (merchants: Merchant[]): string =>
  merchants.map(m => m.merchantName).join(', ')

const stepOneEvent = (
  values: SpendingBucketFormValues,
  categoryName?: string,
): void => {
  allytm.event('customEvent', {
    name: 'createSpendingBucket',
    type: 'userAttribute',
    attr: {
      card: {
        bucketCategory: categoryName,
        bucketName: values.bucketName,
        bucketNameCustom: values.bucketName !== 'categoryName' ? 'Yes' : 'No',
      },
    },
  })
}

const stepTwoEvent = (values: SpendingBucketFormValues): void => {
  allytm.event('customEvent', {
    name: 'spendingBucketPlanYourSpending',
    type: 'userAttribute',
    attr: {
      card: {
        amount: values.targetAmount?.toFixed(2),
        frequency: lookupFrequency(values.frequency),
        date: values.startDate || '',
      },
    },
  })
}

const stepThreeEvent = (values: SpendingBucketFormValues): void => {
  allytm.event('customEvent', {
    name: 'spendingBucketMoneyDistribution',
    type: 'userAttribute',
    attr: {
      card: {
        distributionMethod: values.autoDistributeIndicator,
        manualFunding: values.initialFundAmount,
      },
    },
  })
}

const stepFourEvent = (values: SpendingBucketFormValues): void => {
  allytm.event('customEvent', {
    name: 'trackSpendingInBucket',
    type: 'userAttribute',
    attr: {
      card: {
        merchantsAdded: getMerchantNames(values.merchants),
        numberOfMerchantsAdded: values.merchants.length,
      },
    },
  })
}

const addBucketSubmitEvent = (
  values: SpendingBucketFormValues,
  categoryName: string | undefined,
): void => {
  allytm.event('customEvent', {
    name: 'addSpendingBucket',
    type: 'userAttribute',
    attr: {
      card: {
        category: categoryName,
        amount: values.targetAmount,
        frequency: lookupFrequency(values.frequency),
        date: values.startDate,
        distributionMethod: values.autoDistributeIndicator,
        bucketBalance: values.initialFundAmount,
        merchantTracking: getMerchantNames(values.merchants),
      },
    },
  })
}

const updateBucketSubmitEvent = (
  values: SpendingBucketFormValues,
  categoryName: string | undefined,
): void => {
  allytm.event('customEvent', {
    name: 'editSpendingBucket',
    type: 'userAttribute',
    attr: {
      card: {
        bucketName: values.bucketName,
        category: categoryName,
        amount: values.targetAmount,
        frequency: lookupFrequency(values.frequency),
        date: values.startDate,
        distributionMethod: values.autoDistributeIndicator,
        merchantTracking: getMerchantNames(values.merchants),
      },
    },
  })
}

const distributeMoneySubmitEvent = (
  values: FormValues,
  fromName: string | undefined,
  toName: string | undefined,
): void => {
  allytm.event('customEvent', {
    name: 'spendingBucketsDistributeMoney',
    type: 'userAttribute',
    attr: {
      card: {
        from: fromName,
        to: toName,
        amount: values.amount,
      },
    },
  })
}

export const searchMerchantEvent = (searchTerm: string): void => {
  allytm.event('customEvent', {
    name: 'spendingBucketMerchantsSearched',
    type: 'userAttribute',
    attr: {
      card: {
        merchantsSearched: searchTerm,
      },
    },
  })
}

export const serverErrorEvent = (
  messageDisplayed: string,
  errorCode: string | number | undefined = 'null',
  isFormError = false,
  creationStep?: Step,
): void => {
  allytm.event('customError', messageDisplayed, errorCode, {
    isFormError,
    creationStep,
  })
}

export const recategorizeTransactionSubmitEvent = ({
  fromBucket,
  toBucket,
  alwaysMove,
}: {
  fromBucket: string
  toBucket: string
  alwaysMove: string
}): void => {
  allytm.event('customEvent', {
    name: 'spendingBucketRecategorization',
    type: 'userAttribute',
    attr: {
      card: {
        From: fromBucket,
        To: toBucket,
        AlwaysMove: alwaysMove,
      },
    },
  })
}

export const recategorizeModalImpressionEvent = (
  alwaysOnRadioButtonPresent: string,
): void => {
  allytm.event('impressionEvent' as any, {
    name: 'spendingBucketsRecategorizationImpressionEvent',
    contents: [
      {
        alwaysOnRadioButtonPresent,
      },
    ],
  })
}

export const customizeDefaultTabEvent = (view: string): void => {
  allytm.event('customEvent', {
    name: 'spendingBucketsDefaultView',
    type: 'userAttribute',
    attr: {
      user: {
        defaultView: view,
      },
    },
  })
}

export const customizeDefaultStkTabEvent = (view: string): void => {
  allytm.event('customEvent', {
    name: 'savingsPreferencesDefaultView',
    type: 'userAttribute',
    attr: {
      user: {
        defaultView: view,
      },
    },
  })
}

export const customizeErrorEvent = (view: string): void => {
  allytm.event(
    'customError',
    "We're experiencing a technical hiccup and can't update your preferences at this time. Try again later.",
    `customizeError-${view}`,
    {
      isFormError: true,
    },
  )
}

export const spendingBucketsServerErrors = {
  createFailure: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      true,
      5,
    )
  },
  getCategoriesFailure: (err: Error): void => {
    serverErrorEvent(
      "It's not you, it's us. We're experiencing a technical hiccup. Try again later.",
      err?.message,
      true,
      1,
    )
  },
  getBucketsFailure: (err: Error): void => {
    serverErrorEvent(
      "We can't show your buckets right now.Everything should be up and running again in a bit. Check back later.",
      err?.message,
      false,
    )
  },
  editBucketFailure: (err: Error): void => {
    serverErrorEvent(
      "We're experiencing a technical hiccup. Try editing this bucket again later.",
      err?.message,
      false,
    )
  },
  deleteBucketFailure: (err: Error): void => {
    serverErrorEvent(
      "We're experiencing a technical hiccup. Try deleting this bucket again later.",
      err?.message,
      false,
    )
  },
  searchMerchantsError: (err: Error, isEdit = false): void => {
    serverErrorEvent(
      "We're experiencing a search hiccup, but don't let that derail you from creating a bucket. You can always add businesses later.",
      err?.message,
      true,
      isEdit ? undefined : 4, // step 4 or N/A
    )
  },
  getTransactionsFailure: (err: Error): void => {
    serverErrorEvent(
      "We're experiencing a technical hiccup that's preventing us from showing your transaction details. Check back later.",
      err?.message,
      false,
    )
  },
}

interface SpendingBucketsTagging {
  stepEvent: (step: Step, values: SpendingBucketFormValues) => void
  createBucketSubmitEvent: (values: SpendingBucketFormValues) => void
  editBucketSubmitEvent: (values: SpendingBucketFormValues) => void
  distributeMoneyEvent: (values: FormValues) => void
}

export const useSpendingBucketsTagging = (): SpendingBucketsTagging => {
  const {
    spendingBucketsCategoriesState: { data },
  } = useSpendingBucketCategories()
  const { spendingBucketsState } = useContext(SpendingBucketsContext)

  const stepEvent = (step: Step, values: SpendingBucketFormValues): void => {
    if (step === 1) {
      stepOneEvent(values, lookupCategoryName(values.category, data))
    }
    if (step === 2) {
      stepTwoEvent(values)
    }
    if (step === 3) {
      stepThreeEvent(values)
    }
    if (step === 4) {
      stepFourEvent(values)
    }
  }
  const createBucketSubmitEvent = (values: SpendingBucketFormValues): void =>
    addBucketSubmitEvent(values, lookupCategoryName(values.category, data))

  const editBucketSubmitEvent = (values: SpendingBucketFormValues): void =>
    updateBucketSubmitEvent(values, lookupCategoryName(values.category, data))

  const distributeMoneyEvent = (values: FormValues): void => {
    const findBucket = (bucketId: string): SpendingBucket | undefined =>
      spendingBucketsState.data?.find(
        bucket => bucket.toolkitBucketId === bucketId,
      )

    const toBucketCategoryId = findBucket(values.toBucketId)?.categoryId
    const fromBucketCategoryId = findBucket(values.fromBucketId)?.categoryId
    distributeMoneySubmitEvent(
      values,
      lookupCategoryName(fromBucketCategoryId, data),
      lookupCategoryName(toBucketCategoryId, data),
    )
  }

  return {
    stepEvent,
    createBucketSubmitEvent,
    editBucketSubmitEvent,
    distributeMoneyEvent,
  }
}
